import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Grid,
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import LocalizedInput from "../LocalizedInput";
import { emptyLocalizedFields } from "../../../config/localization";
import TextInput from "../TextInput";
import isEqual from "lodash.isequal";
import VirtualizedAutoCompleteField from "../VirtualizedAutoCompleteField";
import EtimDataType from "../EtimDataType";
import { DataType } from "../../../types/constant";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import { useImageUpload } from "../../../contexts/hooks/useImageUpload";
import { usePreparationData } from "../../../contexts/hooks/usePreparationData";
import { useSearch } from "../../../contexts/hooks/useReusableHook";
import { fetchFeatureGroups } from "../../../services/featureGroupService";
import { fetchClassesSimple } from "../../../services/classService";
import { fetchUnits } from "../../../services/unitService";
import { fetchValues } from "../../../services/valueService";
import AddIncludedFeatureAutoComplete from "../Class/IncludedFeatures/AddIncludedFeatureAutoComplete";
import TabulatedGridCollapsibleV2 from "../TabulatedGridCollapsibleV2";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { RowActions } from "../../../utils/RowActions";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { useNavigate } from "react-router-dom";
import CircularProgressWithLabel from "../CircularProgressWithLabel";
import { useIncludedFeatureJob } from "../../../contexts/hooks/useIncludedFeatureJob";
import { useUnsavedChangesGuard } from "../../../contexts/hooks/useUnsavedChangesGuard";
import { StickyFormActions } from "../StickyFormActions";
import { CardHeading, cardSx } from "../CardHeading";

const switchGroups = [
  {
    caption: "Status",
    switches: [
      { field: "featureIsActive", label: "Active" },
      { field: "featureIsNew", label: "New" },
      { field: "featureIsCustomFeature", label: "Custom" },
    ],
  },
  {
    caption: "Values",
    switches: [{ field: "featureIsMultiValue", label: "Multi-value" }],
  },
  {
    caption: "Shop behavior",
    switches: [
      { field: "featureSearchable", label: "Searchable" },
      { field: "featureFilterable", label: "Filterable" },
      { field: "featureComperable", label: "Comparable" },
      { field: "featureViewable", label: "Viewable" },
      { field: "featureSelectable", label: "Selectable" },
    ],
  },
];

const localizedFields = [
  { field: "featureName", label: "Feature Name" },
  { field: "featureDisplayName", label: "Feature Display Name" },
  { field: "featureDescription", label: "Feature Description" },
  { field: "featureToolTip", label: "Feature Tooltip" },
];

const headers = [
  {
    prop: "id",
    text: "Code",
    withIsNewStatus: true,
    width: 100,
    minWidth: 100,
  },
  {
    prop: "classGroupCode",
    text: "Group Code",
    width: 100,
    minWidth: 100,
  },
  {
    prop: "classDisplayName",
    text: "Name",
  },
];

export const FeatureForm = ({ props, onSave }) => {
  const navigate = useNavigate();
  const { confirmDialog, openImageDialog } = usePreload();

  const userId = "JTiro";

  const [currentFeatureOriginal, setCurrentFeatureOriginal] = useState({});
  const [ready, setReady] = useState(false);
  const { featureGroupOptions, classOptions, unitOptions, valueOptions } =
    usePreparationData({
      sources: ["featureGroups", "classes", "units", "values"],
    });

  const {
    options: valueLocalOptions,
    search: searchValues,
    searching: isValueSearching,
  } = useSearch(valueOptions, fetchValues, {
    displayNameFields: [],
    extraKeys: [],
  });

  const {
    options: unitLocalOptions,
    search: searchUnits,
    searching: isUnitSearching,
  } = useSearch(unitOptions, fetchUnits, {
    displayNameFields: [],
    extraKeys: [],
  });

  const {
    options: featureGroupLocalOptions,
    search: searchFeatureGroups,
    searching: isFeatureGroupSearching,
  } = useSearch(featureGroupOptions, fetchFeatureGroups, {
    displayNameFields: [],
    extraKeys: [],
  });

  const {
    options: classLocalOptions,
    search: searchClasses,
    searching: isClassSearching,
  } = useSearch(classOptions, fetchClassesSimple, {
    displayNameFields: ["classDisplayName", "className"],
    extraKeys: [
      "className",
      "classDisplayName",
      "classDescription",
      "classGroupCode",
    ],
  });

  const defaultFeature = useMemo(() => {
    return {
      featureId: props.row.featureId ?? "",
      featureName: props.row.featureName || emptyLocalizedFields,
      featureDisplayName: props.row.featureDisplayName || emptyLocalizedFields,
      featureDescription: props.row.featureDescription || emptyLocalizedFields,
      featureToolTip: props.row.featureTooltip || emptyLocalizedFields,
      featureGroupCode: props.row.featureGroupCode ?? "",
      featureClassCode: props.row.featureClassCode ?? "",
      featureType: props.row.featureType ?? "",
      featureImage: props.row.featureImage ?? "",
      featureIsActive: props.row.featureIsActive ?? true,
      featureDefaultValues: props.row.selectedDefaultValues ?? "",
      featureDefaultUnit: props.row.selectedDefaultUnit ?? "",
      featureIsCustomFeature: props.row.featureIsCustomFeature ?? false,
      featureIsNew: props.row.featureIsNew ?? true,
      featureIncludedInClassCodes: props.row.featureIncludedInClassCodes ?? [],
      featureIsMultiValue: props.row.featureIsMultiValue ?? false,
      featureSearchable: props.row.featureSearchable ?? false,
      featureFilterable: props.row.featureFilterable ?? false,
      featureComperable: props.row.featureComperable ?? false,
      featureViewable: props.row.featureViewable ?? true,
      featureSelectable: props.row.featureSelectable ?? false,
    };
  }, [props.row]);

  const [currentFeature, setCurrentFeature] = useState({ ...defaultFeature });
  const initializedFeatureId = useRef(null);
  const formStateRef = useRef();
  formStateRef.current = { currentFeature, currentFeatureOriginal };

  const persist = async (values) => {
    if (props.isUpdate && props.relations?.status !== "ready") return false;
    // keep the edits (and the unsaved-changes guard) when the save fails
    const saved = await onSave(values);
    if (!saved) return saved;
    if (saved.success === false) {
      setCurrentFeature((current) => ({
        ...current,
        includedClasses: saved.includedClasses,
      }));
      return false;
    }

    if (!props.isUpdate) {
      setCurrentFeature({ ...defaultFeature });
      setCurrentFeatureOriginal({ ...defaultFeature });
    } else {
      // take the saved row so refreshed class relations (isSelectionNew) stay in sync
      const next = saved.feature ?? (typeof saved === "object" ? saved : values);
      setCurrentFeature(next);
      setCurrentFeatureOriginal(next);
    }
    return saved;
  };

  const { openUploader } = useImageUpload({
    current: currentFeature,
    setCurrent: setCurrentFeature,
    onSave: persist,
    imageField: "featureImage",
    resourceField: "featureResourceId",
  });

  const { startImport, isStarting, isRunning, progress } =
    useIncludedFeatureJob(userId, currentFeature?.featureId);

  const visibleIncludedClasses = useMemo(
    () => currentFeature.includedClasses?.filter((f) => !f.deleted) ?? [],
    [currentFeature.includedClasses],
  );

  const saveText = props?.saveText ?? "Save";

  // Existing relationships come from the relationship API, independently of search options.
  useEffect(() => {
    if (!props.isUpdate || !defaultFeature.featureId || props.relations?.status !== "ready") return;

    // Background reloads (for example, a language change) must not discard edits.
    const { currentFeature: current, currentFeatureOriginal: original } = formStateRef.current;
    if (initializedFeatureId.current === defaultFeature.featureId && !isEqual(current, original)) return;

    const updatedFeature = {
      ...defaultFeature,
      includedClasses: props.relations.data,
    };

    setCurrentFeature(updatedFeature);
    setCurrentFeatureOriginal(updatedFeature);
    initializedFeatureId.current = defaultFeature.featureId;
    setReady(true);
  }, [defaultFeature, props.isUpdate, props.relations]);

  const loading = !!props.isUpdate && (!ready || props.refreshing || props.relations?.status !== "ready");

  const hasChanges = !isEqual(currentFeature, currentFeatureOriginal);
  const { confirmLeave } = useUnsavedChangesGuard(hasChanges);
  useEffect(() => {
    if (!props.leaveGuardRef) return;
    props.leaveGuardRef.current = confirmLeave;
    return () => {
      props.leaveGuardRef.current = null;
    };
  }, [props.leaveGuardRef, confirmLeave]);

  const enableValues = currentFeature.featureType === DataType.ALPHANUMERIC;
  const enableUnit =
    currentFeature.featureType === DataType.NUMERIC ||
    currentFeature.featureType === DataType.RANGE;

  useEffect(() => {
    setCurrentFeature((prev) => ({
      ...prev,
      featureDefaultValues: enableValues ? prev.featureDefaultValues : "",
      featureDefaultUnit: enableUnit ? prev.featureDefaultUnit : "",
    }));
  }, [enableValues, enableUnit]);

  const setField = (field, value) =>
    setCurrentFeature((prev) => ({ ...prev, [field]: value }));

  const handleCancel = async () => {
    if (!(await confirmLeave())) return;
    navigate("/featuresnew");
  };

  const handleRemoveClass = async (row) => {
    const { id, isSelectionNew } = row;

    if (!isSelectionNew) {
      const ok = await confirmDialog.show({
        title: "Remove Relation",
        message: `Are you sure you want to remove the relation of this feature from class ${id}`,
      });

      if (!ok) {
        confirmDialog.close();
        return;
      }
    }

    // functional update: state may have changed while the dialog was open
    setCurrentFeature((prev) => ({
      ...prev,
      includedClasses: isSelectionNew
        ? (prev.includedClasses ?? []).filter((f) => f.id !== id)
        : (prev.includedClasses ?? []).map((f) =>
            f.id === id ? { ...f, deleted: true } : f,
          ),
    }));

    confirmDialog.close();
  };

  const pageRowActions = [
    {
      tooltip: "Remove Class",
      icon: <DeleteForeverRoundedIcon />,
      onClick: (row) => {
        handleRemoveClass(row);
      },
    },
  ];

  const jobActive = isRunning || isStarting;

  return (
    <Box
      aria-busy={loading}
      sx={{
        // keep focused controls clear of the sticky action bar (WCAG 2.4.11)
        "& input, & textarea, & button": {
          scrollMarginBottom: { xs: 168, sm: 96 },
        },
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Stack spacing={3} sx={{ height: "100%" }}>
            <Paper
              component="section"
              variant="outlined"
              aria-labelledby="feature-details-heading"
              sx={cardSx}
            >
              <CardHeading
                id="feature-details-heading"
                title="Feature details"
                helper="Identify the feature and define its type and group."
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextInput
                    label="Feature Id"
                    required
                    disabled={props.isUpdate || loading}
                    helperText={
                      props.isUpdate
                        ? "The feature id can't be changed after creation"
                        : undefined
                    }
                    value={currentFeature.featureId}
                    onChange={(e) => setField("featureId", e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <EtimDataType
                    label="Feature Type"
                    disabled={props.isUpdate || loading}
                    value={currentFeature.featureType}
                    onChange={(e) => setField("featureType", e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <VirtualizedAutoCompleteField
                    options={featureGroupLocalOptions ?? []}
                    limit={1}
                    label="Feature Group"
                    disabled={loading}
                    value={currentFeature.featureGroupCode?.split("|") || []}
                    onChange={(val) =>
                      setField(
                        "featureGroupCode",
                        (Array.isArray(val) ? val : [val]).join("|"),
                      )
                    }
                    searching={isFeatureGroupSearching}
                    onSearch={searchFeatureGroups}
                  />
                </Grid>
                {enableValues && (
                  <Grid size={{ xs: 12 }}>
                    <VirtualizedAutoCompleteField
                      options={valueLocalOptions ?? []}
                      limit={1}
                      disabled={loading}
                      label="Default Values"
                      value={
                        currentFeature.featureDefaultValues?.split("|") || []
                      }
                      onChange={(val) =>
                        setField(
                          "featureDefaultValues",
                          (Array.isArray(val) ? val : [val]).join("|"),
                        )
                      }
                      searching={isValueSearching}
                      onSearch={searchValues}
                    />
                  </Grid>
                )}
                {enableUnit && (
                  <Grid size={{ xs: 12 }}>
                    <VirtualizedAutoCompleteField
                      options={unitLocalOptions ?? []}
                      disabled={loading}
                      limit={1}
                      label="Default Units"
                      value={
                        currentFeature.featureDefaultUnit?.split("|") || []
                      }
                      onChange={(val) =>
                        setField(
                          "featureDefaultUnit",
                          (Array.isArray(val) ? val : [val]).join("|"),
                        )
                      }
                      searching={isUnitSearching}
                      onSearch={searchUnits}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>

            <Paper
              component="section"
              variant="outlined"
              aria-labelledby="feature-localized-heading"
              sx={cardSx}
            >
              <CardHeading
                id="feature-localized-heading"
                title="Localized content"
                helper="Names and texts shown to users in each language."
              />
              <Stack spacing={3}>
                {localizedFields.map(({ field, label }) => (
                  <LocalizedInput
                    key={field}
                    variant="modern"
                    label={label}
                    value={currentFeature[field]}
                    onChange={(val) => setField(field, val)}
                    multiline
                    rows={2}
                    isUpdate={props.isUpdate}
                    disabled={loading}
                  />
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={3} sx={{ height: "100%" }}>
            <Paper
              component="section"
              variant="outlined"
              aria-labelledby="feature-properties-heading"
              sx={cardSx}
            >
              <CardHeading
                id="feature-properties-heading"
                title="Properties"
                helper="Control the feature's status and how it behaves in the shop."
              />
              <Stack spacing={2}>
                {switchGroups.map(({ caption, switches }) => (
                  <Box key={caption}>
                    <Typography variant="overline" color="text.secondary">
                      {caption}
                    </Typography>
                    <FormGroup row>
                      {switches.map(({ field, label }) => (
                        <FormControlLabel
                          key={field}
                          control={
                            <Switch
                              size="small"
                              checked={!!currentFeature[field]}
                              disabled={loading}
                              onChange={(e) =>
                                setField(field, e.target.checked)
                              }
                            />
                          }
                          label={label}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                ))}
              </Stack>
            </Paper>

            <Paper
              component="section"
              variant="outlined"
              aria-labelledby="feature-image-heading"
              sx={cardSx}
            >
              <CardHeading
                id="feature-image-heading"
                title="Image"
                helper="Preview and set the feature image."
              />
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    flexShrink: 0,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.disabled",
                  }}
                >
                  {currentFeature.featureImage ? (
                    <img
                      src={currentFeature.featureImage}
                      alt={`Preview of ${currentFeature.featureId || "feature"} image`}
                      loading="lazy"
                      style={{
                        cursor: "pointer",
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      onClick={() => openImageDialog(currentFeature.featureImage)}
                    />
                  ) : (
                    <ImageOutlinedIcon aria-hidden />
                  )}
                </Box>
                <TextInput
                  label="Image Url"
                  disabled={loading}
                  value={currentFeature.featureImage}
                  onChange={(e) => setField("featureImage", e.target.value)}
                />
              </Stack>
              <Button
                variant="contained"
                color="primary"
                onClick={openUploader}
                disabled={loading}
                startIcon={<AddToPhotosIcon size="inherit" />}
                size="small"
                sx={{ mt: 2 }}
              >
                Set image
              </Button>
            </Paper>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="feature-classes-heading"
            sx={{ p: 3, borderRadius: 2 }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "flex-start" }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    id="feature-classes-heading"
                    component="h2"
                    variant="subtitle1"
                    fontWeight={600}
                  >
                    Included in classes
                  </Typography>
                  {!loading && (
                    <Chip size="small" label={visibleIncludedClasses.length} />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Classes this feature belongs to.
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                {jobActive && (
                  <Stack
                    role="status"
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      Running 'add to all classes' job…
                    </Typography>
                    <CircularProgressWithLabel value={progress} />
                  </Stack>
                )}
                {!loading && <AddIncludedFeatureAutoComplete
                  label="Add Classes"
                  withApplyToAll={{
                    isRunning: jobActive,
                    startImport: startImport,
                    featureId: currentFeature?.featureId,
                    enabled:
                      visibleIncludedClasses.length != classOptions.length,
                  }}
                  options={classLocalOptions}
                  onChange={() => {}}
                  defaultValue={visibleIncludedClasses}
                  onSearch={searchClasses}
                  searching={isClassSearching}
                  callBack={(state) => {
                    if (state.action !== "add") return;
                    setCurrentFeature((prev) => {
                      const current = prev.includedClasses ?? [];
                      const exists = current.some(
                        (f) => f.id === state.item.id,
                      );
                      return {
                        ...prev,
                        includedClasses: exists
                          ? current.map((f) =>
                              f.id === state.item.id
                                ? { ...state.item, deleted: false }
                                : f,
                            )
                          : [
                              ...current,
                              { ...state.item, isSelectionNew: true },
                            ],
                      };
                    });
                  }}
                />}
              </Stack>
            </Stack>
            {props.relations?.status === "error" && (
              <Stack role="alert" direction="row" spacing={2} alignItems="center">
                <Typography color="error">Could not load class relationships. Saving is disabled.</Typography>
                <Button onClick={props.retryRelations}>Retry</Button>
              </Stack>
            )}
            <TabulatedGridCollapsibleV2
              variant="modern"
              headers={headers}
              data={visibleIncludedClasses}
              height={400}
              loading={loading}
              emptyState={
                <Stack
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 8, color: "text.secondary" }}
                >
                  <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
                  <Typography>
                    This feature isn't included in any class yet
                  </Typography>
                </Stack>
              }
              rowActions={(row) => (
                <RowActions row={row} actions={pageRowActions} />
              )}
            />
          </Paper>
        </Grid>
      </Grid>

      <StickyFormActions
        hasChanges={hasChanges}
        saving={props.uploading}
        disabled={loading}
        saveText={saveText}
        onCancel={handleCancel}
        onSave={() => persist(currentFeature)}
      />
    </Box>
  );
};
